// The one place prices live. Billing charges from it and the public page shows it; nothing here pulls in zod, so the landing bundle stays small.

export const PRICE_CURRENCY = "EUR";

// ---------------------------------------------------------------------------
// Plans (docs/questions/s10-signup.md, decisions 1-4, 23, 24, 29, 30 and 34)
// ---------------------------------------------------------------------------

/** From the smallest to the largest: each one has everything the one before it has. */
export const PLAN_CODES = ["start", "crestere", "complet"] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

/**
 * The plan of a workspace that has none written yet, of every workspace billed through its host, and of every one created before checkout
 * asks for a plan: nobody loses what they already use.
 */
export const DEFAULT_PLAN: PlanCode = "complet";

export const planRank = (plan: PlanCode): number => PLAN_CODES.indexOf(plan);
export const isPlanCode = (value: unknown): value is PlanCode => typeof value === "string" && (PLAN_CODES as ReadonlyArray<string>).includes(value);

/**
 * How a plan gets a feature, after Pontimed's feature gating:
 * - `included`: all of it;
 * - `limited`: included up to an amount each month, and billed beyond it (AI in USD, e-mail per location);
 * - `upgrade`: visible, locked, and the way to a plan that has it.
 */
export const FEATURE_ACCESS_KINDS = ["included", "limited", "upgrade"] as const;
export type FeatureAccessKind = (typeof FEATURE_ACCESS_KINDS)[number];

/** What a `limited` amount counts. */
export type FeatureLimitUnit = "ai_usd_month" | "emails_per_location_month";

interface FeatureEntry {
  /** The smallest plan that has it. */
  minPlan: PlanCode;
  /** Set when the plan includes only an amount of it each month. */
  limit?: FeatureLimitUnit;
}

/**
 * Every feature a plan decides, by a code that never changes: routes, MCP tools, the menu and the help documents name these. Adding a
 * feature is one line here, with the smallest plan that has it.
 */
export const PLAN_FEATURES = {
  // Start: content and publishing.
  topics: { minPlan: "start" },
  week_plan: { minPlan: "start" },
  posts: { minPlan: "start" },
  visuals: { minPlan: "start" },
  approvals: { minPlan: "start" },
  calendar: { minPlan: "start" },
  publishing: { minPlan: "start" },
  monday_report: { minPlan: "start" },
  ai: { minPlan: "start", limit: "ai_usd_month" },
  mcp: { minPlan: "start" },
  // Creștere: customers, not only posts.
  leads: { minPlan: "crestere" },
  forms: { minPlan: "crestere" },
  landing_pages: { minPlan: "crestere" },
  customer_messages: { minPlan: "crestere", limit: "emails_per_location_month" },
  journeys: { minPlan: "crestere" },
  pixel: { minPlan: "crestere" },
  consent: { minPlan: "crestere" },
  conversions: { minPlan: "crestere" },
  // Complet: and the ads.
  ads: { minPlan: "complet" },
  ad_test_cells: { minPlan: "complet" },
  budget_guards: { minPlan: "complet" },
  promote_in_zone: { minPlan: "complet" },
  audiences: { minPlan: "complet" },
  ad_catalog: { minPlan: "complet" },
} as const satisfies Record<string, FeatureEntry>;

export type FeatureCode = keyof typeof PLAN_FEATURES;
export const FEATURE_CODES = Object.keys(PLAN_FEATURES) as ReadonlyArray<FeatureCode>;
export const isFeatureCode = (value: unknown): value is FeatureCode => typeof value === "string" && Object.hasOwn(PLAN_FEATURES, value);

export interface PlanDefinition {
  code: PlanCode;
  /** Per location, per month, in cents, before the volume discount. */
  monthlyMinor: number;
  /** Everything this plan has, from the catalog above. */
  features: ReadonlyArray<FeatureCode>;
  /** The AI the plan pays for each month, measured in USD as the AI ledger is. Beyond it, at cost plus `AI_MARKUP_PERCENT`. */
  aiIncludedUsd: number;
  /** E-mails to customers included per location each month. Beyond them, `EMAIL_BLOCK_MINOR` per started `EMAIL_BLOCK` (decision 30). */
  emailsIncludedPerLocation: number;
}

const featuresOf = (plan: PlanCode): ReadonlyArray<FeatureCode> => FEATURE_CODES.filter((code) => planRank(PLAN_FEATURES[code].minPlan) <= planRank(plan));

export const PLANS: Readonly<Record<PlanCode, PlanDefinition>> = {
  start: { code: "start", monthlyMinor: 2900, features: featuresOf("start"), aiIncludedUsd: 5, emailsIncludedPerLocation: 0 },
  crestere: { code: "crestere", monthlyMinor: 4900, features: featuresOf("crestere"), aiIncludedUsd: 8, emailsIncludedPerLocation: 5000 },
  complet: { code: "complet", monthlyMinor: 7900, features: featuresOf("complet"), aiIncludedUsd: 12, emailsIncludedPerLocation: 10000 },
};

/** The step reached applies to every location counted. s10-plans-b counts them per paying company, across its workspaces (decision 34). */
export const VOLUME_DISCOUNTS: ReadonlyArray<{ fromLocations: number; percent: number }> = [
  { fromLocations: 10, percent: 50 },
  { fromLocations: 5, percent: 30 },
  { fromLocations: 2, percent: 15 },
];

export function volumeDiscountPercent(locations: number): number {
  const count = Math.max(0, Math.floor(locations));
  return VOLUME_DISCOUNTS.find((step) => count >= step.fromLocations)?.percent ?? 0;
}

/** One location of a plan, after the discount that `countedLocations` reach. Every step gives whole cents on today's prices. */
export function planLocationPriceMinor(plan: PlanCode, countedLocations: number): number {
  return Math.round((PLANS[plan].monthlyMinor * (100 - volumeDiscountPercent(countedLocations))) / 100);
}

/** A workspace's month: its locations at the plan's price, discounted by the locations counted together (its own, until companies exist). */
export function planMonthlyTotalMinor(plan: PlanCode, locations: number, countedLocations = locations): number {
  const count = Math.max(1, Math.floor(locations));
  return planLocationPriceMinor(plan, Math.max(count, Math.floor(countedLocations))) * count;
}

/** E-mail to customers beyond the included amount: 1 EUR for each started thousand. */
export const EMAIL_BLOCK = 1000;
export const EMAIL_BLOCK_MINOR = 100;

/** The thousands to bill for a month: none up to the included amount, then one for each thousand started. */
export function emailBlocksOver(sent: number, included: number): number {
  const over = Math.max(0, Math.floor(sent) - Math.max(0, Math.floor(included)));
  return Math.ceil(over / EMAIL_BLOCK);
}

// ---------------------------------------------------------------------------
// Beyond the plan
// ---------------------------------------------------------------------------

/** One SMS, in cents. */
export const SMS_UNIT_MINOR = 4;
/** The ledger is in USD and the invoice in EUR. A set rate, so the same ledger always gives the same line. */
export const AI_USD_TO_EUR = 0.92;
/** AI beyond the plan's included amount is passed on at cost plus this, which covers the exchange rate and the payment fees (decision 29). */
export const AI_MARKUP_PERCENT = 10;
/** The default monthly ceiling per business, and what a restaurant's week costs, both as measured in USD by the AI ledger. */
export const AI_CEILING_USD = 20;
export const AI_WEEK_COST_USD = { min: 1, max: 2 } as const;

/** "0.04", or "0,04" where the language writes it so. */
export function smsPriceLabel(decimalMark: "." | "," = "."): string {
  return (SMS_UNIT_MINOR / 100).toFixed(2).replace(".", decimalMark);
}
